import seedDashboard from "../data/seed-dashboard.json";
import { adaptDashboard } from "@adapter";
import {
  buyItem,
  cancelSprint,
  completeSprint,
  pauseSprint,
  resumeSprint,
  sprintPaused,
  sprintDurationMs,
  startSprint,
  tick,
  weakestBuilding,
  type BuildingProgress,
  type TickResult,
  type VillageMetrics,
  type VillageState,
} from "@engine";

const STORAGE_KEY = "life-village-state-v1";

export type Session = {
  /** "seed" when playing from the bundled snapshot, "live" when the Worker answered. */
  source: "seed" | "live";
  metrics: VillageMetrics;
  missing: string[];
  warnings: string[];
  unallocatedMrrUsd: number;
  result: TickResult;
};

function fastMode(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("fast") || import.meta.env.DEV === true && params.has("sprint");
}

export function isFastSprint(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("fast");
}

function readStored(): VillageState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as VillageState;
  } catch {
    return null;
  }
}

export function persistState(state: VillageState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function bootstrapSession(): Session {
  const adapted = adaptDashboard(seedDashboard);
  const stored = readStored();
  const yesterday =
    stored && stored.date === adapted.metrics.date
      ? stored
      : stored && stored.date < adapted.metrics.date
        ? stored
        : null;
  const result = tick(adapted.metrics, yesterday, new Date(), adapted.missing);
  persistState(result.state);
  return {
    source: "seed",
    metrics: adapted.metrics,
    missing: adapted.missing,
    warnings: adapted.warnings,
    unallocatedMrrUsd: adapted.unallocatedMrrUsd,
    result,
  };
}

export async function hydrateSession(): Promise<Session> {
  try {
    const response = await fetch("/api/state", { credentials: "include" });
    if (response.ok) {
      const payload = (await response.json()) as {
        metrics: VillageMetrics;
        missing: string[];
        warnings?: string[];
        unallocatedMrrUsd?: number;
        state: VillageState;
        buildings: BuildingProgress[];
      };
      persistState(payload.state);
      return {
        source: "live",
        metrics: payload.metrics,
        missing: payload.missing,
        warnings: payload.warnings ?? [],
        unallocatedMrrUsd: payload.unallocatedMrrUsd ?? 0,
        result: {
          state: payload.state,
          buildings: payload.buildings,
          missingMetrics: payload.missing,
        },
      };
    }
  } catch {
    // Static / local seed path — Worker and D1 are optional.
  }
  return bootstrapSession();
}

export function purchase(session: Session, itemId: string): Session {
  const bought = buyItem(session.result.state, itemId, session.metrics);
  if (!bought.ok) {
    return session;
  }
  const next: Session = {
    ...session,
    result: { ...session.result, state: bought.state },
  };
  persistState(bought.state);
  void fetch("/api/shop", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ itemId }),
  }).catch(() => undefined);
  return next;
}

export function beginSprint(session: Session, atMs: number): Session {
  const buildingId = weakestBuilding(session.result.state.levels);
  const sprint = startSprint(buildingId, atMs, sprintDurationMs(isFastSprint() || fastMode()));
  const state = { ...session.result.state, sprint };
  persistState(state);
  void fetch("/api/sprint/start", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ buildingId, atMs, durationMs: sprint.durationMs }),
  }).catch(() => undefined);
  return { ...session, result: { ...session.result, state } };
}

export function finishSprint(session: Session, atMs: number): Session {
  const done = completeSprint(session.result.state, atMs);
  if (!done.ok) {
    return session;
  }
  persistState(done.state);
  void fetch("/api/sprint/complete", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ atMs }),
  }).catch(() => undefined);
  return { ...session, result: { ...session.result, state: done.state } };
}

export function togglePause(session: Session, atMs: number): Session {
  const current = session.result.state;
  const state = sprintPaused(current.sprint) ? resumeSprint(current, atMs) : pauseSprint(current, atMs);
  persistState(state);
  return { ...session, result: { ...session.result, state } };
}

export function abortSprint(session: Session): Session {
  const state = cancelSprint(session.result.state);
  persistState(state);
  return { ...session, result: { ...session.result, state } };
}

export function resetLocalVillage(): void {
  localStorage.removeItem(STORAGE_KEY);
}
